<template>
    <v-card flat class="comp-misure">
        <v-card-title class="comp-header pa-4 pb-2">
            <v-icon size="18" class="text-medium-emphasis me-2">mdi-ruler-square</v-icon>
            <span class="text-h6 font-weight-bold text-truncate">Misure</span>
            <v-spacer />
            <v-btn color="primary" size="small" @click="nuovaRiga" prepend-icon="mdi-plus">
                Nuova riga
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 pt-2">
            <div v-if="righeInterne.length === 0 && !loading" class="empty-state">
                <v-icon size="36" class="text-grey-lighten-1 mb-2">mdi-ruler-square</v-icon>
                <p class="text-body-2 text-medium-emphasis ma-0">Nessuna misura inserita</p>
                <v-btn size="small" variant="tonal" color="primary" class="mt-3" prepend-icon="mdi-plus"
                    @click="nuovaRiga">
                    Aggiungi la prima
                </v-btn>
            </div>

            <div v-else class="table-wrap">
                <v-data-table :headers="headersVisibili" :items="righeInterne" :loading="loading" density="compact"
                    hide-default-footer class="misure-table" :items-per-page="-1">

                    <template v-slot:[`item.Rif`]="{ item }">
                        <span class="text-body-2 font-weight-semibold text-primary">{{ item.Rif }}</span>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="row-actions">
                            <v-btn icon size="x-small" variant="text" color="grey-darken-1" @click="modificaRiga(item)">
                                <v-icon size="16">mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="grey-darken-1" @click="duplicaRiga(item)">
                                <v-icon size="16">mdi-content-copy</v-icon>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="error" @click="eliminaRiga(item)">
                                <v-icon size="16">mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </template>

                </v-data-table>
            </div>
        </v-card-text>

        <!-- Dialog crea/modifica -->
        <v-dialog v-model="dialogModifica" max-width="720" persistent>
            <v-card flat>
                <v-card-title class="dialog-header pb-2">
                    <v-row class="ma-0 pa-0">
                        <span class="text-base font-weight-semibold">
                            {{ rigaCorrente.id ? 'Modifica riga' : 'Nuova riga' }}
                        </span>
                        <v-spacer />
                        <v-btn icon size="small" variant="text" @click="chiudiDialog">
                            <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                    </v-row>
                </v-card-title>

                <v-card-text class="pa-3 pt-2">
                    <v-form ref="formRigaRef">
                        <!-- Campi dinamici: solo quelli con visibileMisure = true -->
                        <v-row dense>
                            <template v-for="campo in campiMisureVisibili" :key="campo.campoKey">
                                <v-col :cols="colsCampo(campo)" :sm="colsCampoSm(campo)">
                                    <!-- RIF obbligatorio sempre -->
                                    <v-text-field v-if="campo.campoKey === 'misura_Rif'" v-model="rigaCorrente['Rif']"
                                        label="RIF." :rules="[rules.required]" hide-details="auto" density="compact"
                                        variant="outlined">
                                        <template #prepend-inner>
                                            <v-icon size="15" class="text-medium-emphasis">mdi-pound</v-icon>
                                        </template>
                                    </v-text-field>

                                    <!-- Textarea per note -->
                                    <v-textarea v-else-if="campo.tipoInput === 'textarea'"
                                        v-model="rigaCorrente[campoKeyToModel(campo.campoKey)]" :label="campo.etichetta"
                                        hide-details density="compact" variant="outlined" :rows="3" autoGrow
                                        :max-rows="6" />

                                    <!-- Number -->
                                    <v-text-field v-else-if="campo.tipoInput === 'number'"
                                        v-model="rigaCorrente[campoKeyToModel(campo.campoKey)]" :label="campo.etichetta"
                                        type="number" hide-details density="compact" variant="outlined" />

                                    <!-- Text default -->
                                    <v-text-field v-else v-model="rigaCorrente[campoKeyToModel(campo.campoKey)]"
                                        :label="campo.etichetta" hide-details density="compact" variant="outlined" />
                                </v-col>
                            </template>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn size="small" variant="text" @click="chiudiDialog">Annulla</v-btn>
                    <v-btn size="small" color="primary" :loading="loading" @click="salvaRiga"
                        prepend-icon="mdi-content-save">
                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { crea_riga_misura, modifica_riga_misura, elimina_riga_misura } from '@/api/api.misure.js'
import { get_configurazione } from '@/api/config/api.campi.js'

// Mappa campoKey (misura_Xxx) → property dell'oggetto riga (camelCase)
const KEY_MAP = {
    misura_Rif: 'Rif', misura_Tipo: 'tipo', misura_Qta: 'Qta',
    misura_L: 'L', misura_H: 'H', misura_NBat: 'NBat',
    misura_Spinta: 'Spinta', misura_Maz: 'Maz', misura_Ar: 'Ar',
    misura_Serratura: 'Serratura', misura_Zanz: 'Zanz', misura_Vetro: 'Vetro',
    misura_AntonePersiana: 'AntonePersiana', misura_ScuroInterno: 'ScuroInterno',
    misura_Batt: 'Batt', misura_Note: 'Note'
}

export default {
    name: 'TabellaMisure',

    props: {
        ordineId: { type: [String, Number], required: true },
        righe: { type: Array, default: () => [] },
        modulo: { type: String, default: 'serramenti' }
    },

    emits: ['righe-aggiornate'],

    setup(props, { emit }) {
        const loading = ref(false)
        const dialogModifica = ref(false)
        const formRigaRef = ref(null)
        const righeInterne = ref([...props.righe])

        // Configurazione dinamica campi misura
        const campiMisureConfig = ref([]) // tutti i campi misura_*

        const campiMisureVisibili = computed(() =>
            campiMisureConfig.value
                .filter(c => c.visibileMisure)
                .sort((a, b) => a.ordinamento - b.ordinamento)
        )

        // Colonne tabella dinamiche
        const headersVisibili = computed(() => {
            const cols = campiMisureVisibili.value
                // escludi note dalla tabella (troppo lunga), tienila solo in dialog
                .filter(c => c.campoKey !== 'misura_Note')
                .map(c => ({
                    title: c.etichetta,
                    value: KEY_MAP[c.campoKey] || c.campoKey,
                    sortable: c.campoKey === 'misura_Rif',
                    width: colWidth(c)
                }))
            // colonna azioni sempre in fondo
            cols.push({ title: '', value: 'actions', sortable: false, width: '100', align: 'end' })
            return cols
        })

        const rigaVuota = computed(() => {
            const obj = { id: null }
            Object.values(KEY_MAP).forEach(k => { obj[k] = '' })
            return obj
        })

        const rigaCorrente = ref({ id: null })

        const rules = { required: v => !!v || 'Campo obbligatorio' }

        // ── Caricamento configurazione ─────────────────────────────────────
        const caricaConfig = async () => {
            try {
                const res = await get_configurazione(props.modulo)
                const sezioni = res.data?.sezioni || []
                // Raccolgo tutti i campi misura_* da tutte le sezioni
                const tutti = sezioni.flatMap(s => s.campi).filter(c => c.campoKey.startsWith('misura_'))
                campiMisureConfig.value = tutti
            } catch (e) {
                console.error('Errore caricamento config misure:', e)
            }
        }

        onMounted(caricaConfig)

        watch(() => props.righe, (v) => { righeInterne.value = [...v] }, { deep: true })

        // ── CRUD righe ────────────────────────────────────────────────────
        const nuovaRiga = () => {
            rigaCorrente.value = { ...rigaVuota.value }
            dialogModifica.value = true
        }

        const modificaRiga = (riga) => {
            rigaCorrente.value = { ...riga }
            dialogModifica.value = true
        }

        const duplicaRiga = (riga) => {
            rigaCorrente.value = { ...riga, id: null }
            dialogModifica.value = true
        }

        const salvaRiga = async () => {
            const { valid } = await formRigaRef.value.validate()
            if (!valid) return
            loading.value = true
            try {
                if (rigaCorrente.value.id) {
                    await modifica_riga_misura(props.ordineId, rigaCorrente.value.id, rigaCorrente.value)
                    const idx = righeInterne.value.findIndex(r => r.id === rigaCorrente.value.id)
                    righeInterne.value[idx] = { ...rigaCorrente.value }
                } else {
                    const res = await crea_riga_misura(props.ordineId, rigaCorrente.value)
                    righeInterne.value.push(res.data || res)
                }
                emit('righe-aggiornate', righeInterne.value)
                chiudiDialog()
            } catch (e) {
                console.error('Errore salvataggio riga:', e)
            }
            loading.value = false
        }

        const eliminaRiga = async (riga) => {
            if (!confirm('Eliminare questa riga?')) return
            loading.value = true
            try {
                await elimina_riga_misura(props.ordineId, riga.id)
                righeInterne.value = righeInterne.value.filter(r => r.id !== riga.id)
                emit('righe-aggiornate', righeInterne.value)
            } catch (e) { console.error('Errore eliminazione riga:', e) }
            loading.value = false
        }

        const chiudiDialog = () => {
            dialogModifica.value = false
            rigaCorrente.value = { ...rigaVuota.value }
        }

        // ── Helpers ───────────────────────────────────────────────────────
        const campoKeyToModel = (key) => KEY_MAP[key] || key

        const colWidth = (campo) => {
            if (['misura_L', 'misura_H', 'misura_Qta', 'misura_NBat', 'misura_Maz', 'misura_Ar'].includes(campo.campoKey)) return '70'
            if (campo.campoKey === 'misura_Rif') return '70'
            return '90'
        }

        const colsCampo = (campo) => {
            if (campo.tipoInput === 'textarea') return 12
            return 12
        }

        const colsCampoSm = (campo) => {
            if (campo.tipoInput === 'textarea') return 12
            if (campo.tipoInput === 'number') return 3
            return 3
        }

        return {
            loading, dialogModifica, formRigaRef, righeInterne,
            campiMisureVisibili, headersVisibili,
            rigaCorrente, rules,
            nuovaRiga, modificaRiga, duplicaRiga,
            salvaRiga, eliminaRiga, chiudiDialog,
            campoKeyToModel, colsCampo, colsCampoSm
        }
    }
}
</script>

<style scoped>
.comp-misure {
    margin: 0 auto;
}

.comp-header {
    border-bottom: 1px solid rgba(0, 0, 0, .08);
    background: #fafafa;
    border-radius: 8px 8px 0 0;
}

.empty-state {
    text-align: center;
    padding: 28px 0 8px;
}

.table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid rgba(0, 0, 0, .08);
    border-radius: 8px;
}

.misure-table {
    min-width: 600px;
    font-size: .82rem;
}

.misure-table :deep(thead tr th) {
    background: #f7f7f8 !important;
    border-bottom: 1px solid rgba(0, 0, 0, .1) !important;
    font-size: .75rem !important;
    font-weight: 600;
    color: #5a5a6e;
    text-transform: uppercase;
    letter-spacing: .4px;
    height: 36px !important;
    white-space: nowrap;
}

.misure-table :deep(tbody tr:nth-child(even)) {
    background: #fafafa;
}

.misure-table :deep(tbody tr:hover) {
    background: #eef0f3 !important;
}

.misure-table :deep(tbody td) {
    height: 36px !important;
    padding: 0 10px !important;
    white-space: nowrap;
}

.row-actions {
    display: flex;
    gap: 2px;
    justify-content: flex-end;
}

.dialog-header {
    border-bottom: 1px solid rgba(0, 0, 0, .06);
    background: #f7f7f8;
    border-radius: 8px 8px 0 0;
}
</style>